import { LazyLoadComponent } from 'react-lazy-load-image-component';

import Loader from '~components/generic/loader/Loader';
import { getImageUrlForBreakpoint, imageBreakpoints } from '~services/responsive-helpers';
import { getAbsoluteUrl } from '~store/getter';
import pictureStyles from './PictureStyles.css';

export type PictureProps = {
  proportions: string;
  data: Entity;
  origData: string;
  placeholder?: boolean;
  lazyLoad?: boolean;
  showOriginal?: boolean;
};

export const Picture = ({ data, origData, proportions, placeholder, lazyLoad, showOriginal }: PictureProps) => {
  let classes = pictureStyles.container;

  // if image data isn't available yet, render a dummy element with the same
  // classes, this will reduce the amounts of repaints necessary for picture
  // elements with predefined image ratios (which are also defined as classes)
  if (!data && origData) {
    return (
      <div className={classes}>
        <Loader absoluteCenter />
      </div>
    );
  }
  if (!data && !origData && placeholder) {
    return (
      <div className={classes}>
        <div className="c-image-placeholder" />
      </div>
    );
  }

  if (!data) {
    return <div className={classes} />;
  }

  // no image styles if original img should be shown
  let addClasses = !showOriginal;

  // show original images for gifs, but keep classes
  if (data.attributes.filemime && data.attributes.filemime === 'image/gif') {
    showOriginal = true;
    addClasses = true;
    classes = 'o-image u-fullwidth uncropped-gif';
  }

  // render the image if original img should be shown
  if (!data.meta || !data.meta.derivatives || showOriginal) {
    return <img className={addClasses ? classes : ''} src={getAbsoluteUrl(data.attributes.url)} alt="" />;
  }

  // image styles, render the picture element
  const fileMime = data.attributes.filemime;
  const content = (
    <picture className={classes}>
      {imageBreakpoints.map(bp => {
        const bpQuery = bp[0];
        const bpName = bp[1];
        const imageUrl = getImageUrlForBreakpoint(data, proportions, bpName);

        if (bpQuery === 'fallback') {
          return <img key={bpQuery} alt="" src={imageUrl} className="o-image u-fullwidth" />;
        }
        return <source key={bpQuery} srcSet={`${imageUrl} 1x`} media={bpQuery} type={fileMime} />;
      })}
    </picture>
  );

  if (lazyLoad) {
    return <LazyLoadComponent threshold={300}>{content}</LazyLoadComponent>;
  }
  return content;
};
